import React, { useState } from "react";
import Input from "../../components/Input/Input";
import { useToast } from "../../components/Toaster/use-toast";
import { requestBusinessDemo } from "../../api/businessApi";
import {
  COUNTRY_DIAL_PREFIX,
  COUNTRY_OPTIONS,
  DEFAULT_COUNTRY_CODE,
  DEFAULT_PHONE_PREFIX,
} from "./countryPhoneOptions";
import "./BusinessPage.css";

export default function BusinessPage() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    workEmail: "",
    countryCode: DEFAULT_COUNTRY_CODE,
    phone: DEFAULT_PHONE_PREFIX,
    jobTitle: "",
    companyName: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (e) => {
    const { value } = e.target;
    const nextPrefix = COUNTRY_DIAL_PREFIX[value] || DEFAULT_PHONE_PREFIX;

    setForm((prev) => {
      const oldPrefix = COUNTRY_DIAL_PREFIX[prev.countryCode] || DEFAULT_PHONE_PREFIX;
      const trimmed = String(prev.phone || "").trim();
      const rest = trimmed.startsWith(oldPrefix)
        ? trimmed.slice(oldPrefix.length).trimStart()
        : "";
      const nextPhone = rest ? `${nextPrefix} ${rest}` : nextPrefix;

      return {
        ...prev,
        countryCode: value,
        phone: nextPhone,
      };
    });
  };

  const handlePhoneChange = (e) => {
    const raw = String(e.target.value || "");
    const prefix = COUNTRY_DIAL_PREFIX[form.countryCode] || DEFAULT_PHONE_PREFIX;
    const fallback = `${prefix} `;

    if (!raw.trim()) {
      setForm((prev) => ({ ...prev, phone: fallback }));
      return;
    }

    if (raw.startsWith(prefix)) {
      setForm((prev) => ({ ...prev, phone: raw }));
      return;
    }

    // Prevent removing/changing the prefilled area code while allowing local number edits.
    const localPart = raw.replace(/^\+?\d+\s*/, "").trimStart();
    setForm((prev) => ({ ...prev, phone: localPart ? `${prefix} ${localPart}` : fallback }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);
      await requestBusinessDemo({
        firstName: form.firstName,
        lastName: form.lastName,
        workEmail: form.workEmail,
        countryCode: form.countryCode,
        phone: form.phone,
        jobTitle: form.jobTitle,
        companyName: form.companyName,
        message: form.message || "",
      });

      toast({
        title: "Request received",
        description: "Thanks. Our team will contact you shortly.",
      });

      setForm({
        firstName: "",
        lastName: "",
        workEmail: "",
        countryCode: DEFAULT_COUNTRY_CODE,
        phone: DEFAULT_PHONE_PREFIX,
        jobTitle: "",
        companyName: "",
        message: "",
      });
    } catch (err) {
      toast({
        title: "Unable to submit request",
        description: err?.message || "Please try again shortly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="business-page">
      <section className="business-hero">
        <div className="business-hero-card">
          <h1 className="business-title">
            <strong>Telome</strong> for business
          </h1>
          <p className="business-subtitle">
            Bring structured AI conversation practice to your teams.
          </p>
        </div>
      </section>

      <section className="business-form-section">
        <div className="business-form-wrap">
          <h2 className="business-form-title">Request a demo to learn more about telome</h2>

          <form className="business-form" onSubmit={handleSubmit}>
            <div className="business-grid business-grid-2">
              <div>
                <label className="business-label" htmlFor="firstName">
                  First name <span className="business-required">*</span>
                </label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  clearable
                  required
                />
              </div>
              <div>
                <label className="business-label" htmlFor="lastName">
                  Last name <span className="business-required">*</span>
                </label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  clearable
                  required
                />
              </div>
            </div>

            <div className="business-grid business-grid-2">
              <div>
                <label className="business-label" htmlFor="workEmail">
                  Work email <span className="business-required">*</span>
                </label>
                <Input
                  id="workEmail"
                  name="workEmail"
                  type="email"
                  value={form.workEmail}
                  onChange={handleChange}
                  clearable
                  required
                />
              </div>
              <div>
                <label className="business-label" htmlFor="phone">
                  Phone number <span className="business-required">*</span>
                </label>
                <div className="business-phone-row">
                  <select
                    className="business-area-code"
                    name="countryCode"
                    value={form.countryCode}
                    onChange={handleCountryChange}
                    aria-label="Country code"
                    required
                  >
                    {COUNTRY_OPTIONS.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <Input
                    id="phone"
                    className="business-phone-input"
                    name="phone"
                    value={form.phone}
                    onChange={handlePhoneChange}
                    clearable
                    required
                  />
                </div>
              </div>
            </div>

            <div className="business-grid business-grid-2">
              <div>
                <label className="business-label" htmlFor="jobTitle">
                  Job title <span className="business-required">*</span>
                </label>
                <Input
                  id="jobTitle"
                  name="jobTitle"
                  value={form.jobTitle}
                  onChange={handleChange}
                  clearable
                  required
                />
              </div>
              <div>
                <label className="business-label" htmlFor="companyName">
                  Company name <span className="business-required">*</span>
                </label>
                <Input
                  id="companyName"
                  name="companyName"
                  value={form.companyName}
                  onChange={handleChange}
                  clearable
                  required
                />
              </div>
            </div>

            <div>
              <label className="business-label" htmlFor="message">
                Tell us more about your interest in Telome
              </label>
              <textarea
                id="message"
                className="business-message"
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={5}
              />
            </div>

            <button type="submit" className="business-submit-btn" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit request"}
            </button>

            <p className="business-fineprint">
              By submitting this form, you agree that we may use the data you provide to contact you
              with information related to your request/submission and telome&apos;s products and services.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
